import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

// 스크롤 진입 시 한 번만 애니메이션 트리거하는 훅 (Contact / News와 동일 패턴)
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, inView];
};

const Product = () => {
  const [productList, setProductList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 9;
  const navigate = useNavigate();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(""); // ''(전체), '1', '2', '3'

  // 애니메이션용 훅
  const [textRef, textInView] = useInView(0.25);
  const [tabRef, tabInView] = useInView(0.2);
  const [gridRef, gridInView] = useInView(0.2);
  const [pagerRef, pagerInView] = useInView(0.15);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(
          "https://www.medicode.co.kr:8445/api/get/board_list",
          {
            params: {
              cate: "product",
              subCategory: activeTab,
              page: currentPage,
              pageSize: itemsPerPage,
            },
          }
        );
        setProductList(response.data.data);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        console.error("Error fetching product data:", error);
      }
    };
    fetchProduct();
  }, [currentPage, activeTab]);

  useEffect(() => {
    // URL 파라미터가 변경되면 activeTab과 currentPage 초기화
    setActiveTab(tab || "");
    setCurrentPage(1);
  }, [tab]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0); // 페이지 최상위로 스크롤
  };

  const handleTabChange = (newTab) => {
    navigate(`/board/product/${newTab || ""}`); // URL에 탭 번호 추가
  };

  const handleFirstPage = () => {
    paginate(1);
  };

  const handleLastPage = () => {
    paginate(totalPages);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      paginate(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };

  const handleDetailClick = (idx) => {
    navigate(`/board/product/detail/${idx}`); // 상세 페이지로 이동
  };

  return (
    <div className="product-main-wrap">
      <div className="product-wrap">
        {/* 상단 Hero – 애니메이션 없음 (Contact / News와 동일 컨셉) */}
        <div className="product-top">
          <div className="page-tit">
            <div className="product-wrapper">
              <h3>당신을 위한 건강검진상품, 지금 바로 확인해보세요.</h3>
              <h2>PRODUCT</h2>
            </div>
          </div>
          <div className="img-wrap"></div>
        </div>

        {/* 상단 설명 영역 + 세로 구분선 */}
        <div className="product-box">
          <div
            className={`product-txt-wrap fade-section ${
              textInView ? "is-visible" : ""
            }`}
            ref={textRef}
          >
            <h2>
              Discover Our <span>Products</span>
            </h2>
            <p className="desc1">
              메디코드의 다양한 건강검진 상품을 한 곳에서 만나보세요.
            </p>
            <p className="desc3">
              라이프스타일과 건강 상태에 맞춘 맞춤형 상품들로 고객님의 건강
              여정을 함께합니다.
            </p>
          </div>

          <div
            className={`product-divider fade-section ${
              textInView ? "is-visible" : ""
            }`}
          ></div>

          <div
            className={`product-us-input fade-section ${
              textInView ? "is-visible" : ""
            }`}
          >
            상품 목록
          </div>
        </div>

        {/* 탭 메뉴 */}
        <div
          className={`product-tab-menu-wrapper fade-section ${
            tabInView ? "is-visible" : ""
          }`}
          ref={tabRef}
        >
          <div className="product-tab-menu">
            <button
              className={`product-tab ${activeTab === "" ? "active" : ""}`}
              onClick={() => handleTabChange("")}
            >
              전체
            </button>
            <button
              className={`product-tab ${activeTab === "1" ? "active" : ""}`}
              onClick={() => handleTabChange("1")}
            >
              건강검진
            </button>
            <button
              className={`product-tab ${activeTab === "2" ? "active" : ""}`}
              onClick={() => handleTabChange("2")}
            >
              [준비중] 뷰티(줄기세포)
            </button>
            <button
              className={`product-tab ${activeTab === "3" ? "active" : ""}`}
              onClick={() => handleTabChange("3")}
            >
              [준비중] 항노화(줄기세포)
            </button>
          </div>
          <div
            className={`product-us-input fade-section ${
              textInView ? "is-visible" : ""
            }`}
          >
            <p className="product-us-title">
              원하시는 <span>상품</span>을 클릭하면, 포함된 검진 항목을 한눈에
              확인하실 수 있습니다.
            </p>
            <p className="product-us-sub">
              상품 상세 페이지에서 검사 구성에 대해 자세히 안내해 드립니다.
            </p>
          </div>
        </div>

        {/* 상품 그리드 */}
        <div
          className={`product-grid fade-section ${
            gridInView ? "is-visible" : ""
          }`}
          ref={gridRef}
        >
          {activeTab === "2" || activeTab === "3" ? (
            <div className="coming-soon">
              <p>해당 카테고리의 상품은 준비 중입니다.</p>
              <p>곧 더 다양한 서비스를 선보이겠습니다.</p>
            </div>
          ) : (
            productList.map((product, index) => (
              <div className="product-box" key={index}>
                <div className="image-container">
                  <img
                    src={`https://www.medicode.co.kr:8445/uploads/${product.img1}`}
                    alt={product.title}
                  />
                  <div
                    className="product-hover"
                    onClick={() => handleDetailClick(product.idx)}
                  >
                    <p>상품 상세보기</p>
                  </div>
                </div>
                {/* 향후 제목/설명 쓰고 싶으면 여기 product-content 넣으면 됨 */}
              </div>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        <div className="pagination" ref={pagerRef}>
          <button onClick={handleFirstPage} disabled={currentPage === 1}>
            {"<<"}
          </button>
          <button onClick={handlePreviousPage} disabled={currentPage === 1}>
            {"<"}
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => paginate(i + 1)}
              className={currentPage === i + 1 ? "active" : ""}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
          <button
            onClick={handleLastPage}
            disabled={currentPage === totalPages}
          >
            {">>"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Product;
